import Vue from 'vue'
import App from './App.vue'
import VueRouter from 'vue-router'
import Vuelidate from 'vuelidate'
import axios from './api-service'
import routes from './routes'
import store from './store'
import encryptStorage from './secure-storage'
import GoogleAuth from './plugins/google-auth'
import Toast from 'vue-toastification'
import Toasters from './mixins/toasters'
import "vue-toastification/dist/index.css"
import moment from 'moment'

Vue.use(VueRouter)
Vue.use(Vuelidate)
Vue.use(GoogleAuth)
Vue.use(Toast, {
  transition: "Vue-Toastification__fade",
  maxToasts: 8
})

Vue.mixin(Toasters)

const scrollBehavior = () => {
  return {
    x: 0,
    y: 0
  }
}

const router = new VueRouter({
  routes,
  scrollBehavior,
  mode: 'history'
});

// Suppress NavigationAborted errors caused by navigation guards calling next(false)
const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => {
    if (err && err.name === 'NavigationDuplicated') return;
    if (err && err.message && err.message.includes('Navigation aborted')) return;
    throw err;
  });
};

// Check if a question-level dependency is met
function isQuestionApplicable(q, allQuestions) {
  if (!q.dependentQuestionId) return true;
  let parent = allQuestions.find((p) => p.questionId === q.dependentQuestionId);
  if (!parent) return true;
  if (parent.questionType === "MultipleChoice") {
    let validAnswers = q.dependentQuestionAnswer
      ? q.dependentQuestionAnswer.split("||")
      : [];
    return (
      parent.questionAnswer?.length &&
      parent.questionAnswer.some((r) => validAnswers.includes(r))
    );
  }
  return parent.questionAnswer === q.dependentQuestionAnswer;
}

// Check if a field inside address/employment data is applicable (local + cross-store)
function isFieldApplicable(field, siblingFields, allQuestions) {
  if (!field.dependentQuestionId && !field.dependentQuestionAnswer) return true;
  let parent = siblingFields.find(
    (f) => f.questionId === field.dependentQuestionId
  );
  if (!parent) {
    let globalParent = allQuestions.find(
      (p) => p.questionId === field.dependentQuestionId
    );
    if (!globalParent) return true;
    if (globalParent.questionType === "MultipleChoice") {
      let validAnswers = field.dependentQuestionAnswer
        ? field.dependentQuestionAnswer.split("||")
        : [];
      return (
        globalParent.questionAnswer?.length &&
        globalParent.questionAnswer.some((r) => validAnswers.includes(r))
      );
    }
    return globalParent.questionAnswer === field.dependentQuestionAnswer;
  }
  if (
    field.dependentQuestionAnswer &&
    field.dependentQuestionAnswer.indexOf("||") > -1
  ) {
    let validAnswers = field.dependentQuestionAnswer.split("||");
    if (Array.isArray(parent.questionAnswer)) {
      return parent.questionAnswer.some((r) => validAnswers.includes(r));
    }
    return validAnswers.includes(parent.questionAnswer);
  }
  return parent.questionAnswer === field.dependentQuestionAnswer;
}

// Get list of sections with incomplete mandatory questions
function getIncompleteSections(storeRef) {
  let allQuestions = storeRef.getters.getQuestions || [];
  let allAddresses = storeRef.getters.getAllAddresses || [];
  let allEmployment = storeRef.getters.getAllEmployment || [];
  let appSections = storeRef.getters.getSections || [];

  let sectionNames = appSections
    .filter((s) => {
      if (!s.questionId || !s.questionAnswer) return true;
      return allQuestions.some(
        (q) => q.questionId === s.questionId && q.questionAnswer === s.questionAnswer
      );
    })
    .map((s) => s.sectionName);

  let sections = [];

  sectionNames.forEach((sectionName) => {
    let sectionQuestions = allQuestions.filter(
      (q) => q.sectionName === sectionName
    );
    if (!sectionQuestions.length) return;

    // Check address sections (only if the address question's dependency is met)
    let addressQuestion = sectionQuestions.find(
      (q) => q.apiName === "AddressAPI" && isQuestionApplicable(q, allQuestions)
    );
    if (addressQuestion) {
      let addressSection = allAddresses.find(
        (a) => a.sectionName === sectionName
      );
      if (
        !addressSection ||
        !addressSection.addressDetails ||
        !addressSection.addressDetails.length
      ) {
        sections.push({ sectionName });
        return;
      }
      let hasIncompleteAddress = addressSection.addressDetails.some(
        (entry) =>
          entry.addressData &&
          entry.addressData.some(
            (field) =>
              field.isMandatory &&
              isFieldApplicable(field, entry.addressData, allQuestions) &&
              (field.questionAnswer === null ||
                field.questionAnswer === "" ||
                field.questionAnswer === undefined)
          )
      );
      if (hasIncompleteAddress) {
        sections.push({ sectionName });
        return;
      }

      // Check 2-year (24-month) address history for Personal Information / Co-Applicant Info
      if (
        sectionName === "Personal Information" ||
        sectionName === "Co-Applicant Info"
      ) {
        let twoYearsAgo = moment().subtract(2, "years").format("M/D/YYYY");
        let hasOldEnoughAddress = addressSection.addressDetails.some(
          (entry) => {
            let moveInDate = entry.addressData?.find(
              (f) =>
                f.questionFieldMapping === "move_in_date__c" ||
                f.questionFieldMapping === "candidoem__move_in_date__c"
            );
            return (
              moveInDate?.questionAnswer &&
              moment(moveInDate.questionAnswer).isBefore(twoYearsAgo)
            );
          }
        );
        if (!hasOldEnoughAddress) {
          sections.push({ sectionName, reason: "addressHistory" });
          return;
        }
      }
    }

    // Check employment sections (only if the employment question's dependency is met)
    let employmentQuestion = sectionQuestions.find(
      (q) => q.apiName === "EmploymentInfo" && isQuestionApplicable(q, allQuestions)
    );
    if (employmentQuestion) {
      let employmentSection = allEmployment.find(
        (e) => e.sectionName === sectionName
      );
      if (
        !employmentSection ||
        !employmentSection.employmentInfoDetails ||
        !employmentSection.employmentInfoDetails.length
      ) {
        sections.push({ sectionName });
        return;
      }
      let hasIncompleteEmployment = employmentSection.employmentInfoDetails.some(
        (entry) =>
          entry.employmentInfoData &&
          entry.employmentInfoData.some(
            (field) =>
              field.isMandatory &&
              isFieldApplicable(field, entry.employmentInfoData, allQuestions) &&
              (field.questionAnswer === null ||
                field.questionAnswer === "" ||
                field.questionAnswer === undefined)
          )
      );
      if (hasIncompleteEmployment) {
        sections.push({ sectionName });
        return;
      }
      // Check 2-year employment history for Annual Income / Co-Applicant Annual Income
      if (
        sectionName === "Annual Income" ||
        sectionName === "Co-Applicant Annual Income"
      ) {
        let twoYearsAgo = moment().subtract(2, "years").format("M/D/YYYY");
        let hasOldEnoughEmployment = employmentSection.employmentInfoDetails.some(
          (entry) => {
            let startDate = entry.employmentInfoData?.find(
              (f) =>
                f.questionFieldMapping === "start_date__c" ||
                f.questionFieldMapping === "candidoem__start_date__c"
            );
            return (
              startDate?.questionAnswer &&
              moment(startDate.questionAnswer).isBefore(twoYearsAgo)
            );
          }
        );
        if (!hasOldEnoughEmployment) {
          sections.push({ sectionName, reason: "employmentHistory" });
          return;
        }
      }
    }

    // Check regular mandatory questions
    let mandatoryQuestions = allQuestions.filter((q) => {
      if (q.sectionName !== sectionName) return false;
      if (!q.isMandatory) return false;
      if (q.apiName === "AddressAPI" || q.apiName === "EmploymentInfo")
        return false;
      if (!isQuestionApplicable(q, allQuestions)) return false;
      return true;
    });

    let hasUnanswered = mandatoryQuestions.some((q) => {
      if (
        q.questionAnswer === null ||
        q.questionAnswer === "" ||
        (Array.isArray(q.questionAnswer) && q.questionAnswer.length === 0)
      )
        return true;
      // Check co-applicant answer if applicable
      if (q.questionCoFieldMapping && q.coDependentQuestionId) {
        let coParent = allQuestions.find(
          (p) => p.questionId === q.coDependentQuestionId
        );
        if (
          coParent &&
          coParent.questionAnswer === q.coDependentQuestionAnswer
        ) {
          if (
            q.coQuestionAnswer === null ||
            q.coQuestionAnswer === "" ||
            q.coQuestionAnswer === undefined
          )
            return true;
        }
      }
      return false;
    });

    if (hasUnanswered) {
      sections.push({ sectionName });
    }
  });

  return sections;
}

router.beforeEach((to, from, next) => {
  let authenticated = store.getters.getIsAuthenticated;
  let homePageTailUrl = store.getters.getHomePageTailUrl;

  // Validate before allowing navigation to submit-application
  if (to.path === '/submit-application') {
    let incomplete = getIncompleteSections(store);
    if (incomplete.length > 0) {
      store.dispatch('fetchValidationModalSections', incomplete);
      // Reset activeQuestion back to last valid index if it overshot
      let maxIndex = store.getters.getQuestions.length;
      if (store.getters.getActiveQuestion > maxIndex) {
        store.dispatch('fetchActiveQuestion', maxIndex);
      }
      next(false);
      return;
    }
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authenticated) {
      if (homePageTailUrl) {
        next({ path: '/' + homePageTailUrl });
      } else {
        next({ path: '/' });
      }
    } else {
      next();
    }
  } else {
    next();
  }
});

Vue.config.devtools = false
Vue.config.productionTip = false

Vue.prototype.$http = axios
Vue.prototype.$localStore = encryptStorage;

new Vue({
  router,
  store,
  render: h => h(App),
}).$mount('#app')
